<!-- This component displays a list item with a folder and further meta data like date read, date updated.  -->
<!-- Details can be displayed by a LONG-CLICK and slide in                                                    -->
<!-- Favorites can be set/unset. An unread item is marked in bold text with an additional badge               -->
<template>
  <div class="file pt-4" :class="`file-wrapper-${themeName} ${isSelected ? 'selected-' + themeName : ''}`" @click="onItemSelected">
    <div class="op-left" @click.stop.prevent="">
      <!-- Item with teaserUrl on left -->
      <!--      <div v-if="teaserUrl" class="elevation-1 teaser-img" :style="`background:URL(${teaserUrl})`"></div>-->
      <div v-if="teaserUrl" class="avatar">
        <v-avatar size="35" class="ml-2 mt-0 avatar-img">
          <v-icon>mdi-image</v-icon>
        </v-avatar>
      </div>

      <div v-else-if="avatarUrl" class="avatar">
        <v-avatar size="35" :color="theme.avatarBg" class="mr-2 mt-2 avatar-img" @click.stop="$emit('onAvatarClick')">
          <img alt="avatar" :src="avatarUrl" />
        </v-avatar>
      </div>

      <!-- Icon -->
      <v-icon v-else-if="itemIcon" class="file-icon" :color="mx_listIconColor">{{ itemIcon }}</v-icon>

      <template v-if="!ignoreReadState">
        <!-- badges and dots, don't change order -->
        <!-- icon badge -->
        <div v-if="isMandatoryToRead" class="icon-badge">
          <v-icon x-small :color="isMandatoryToReadAndRead(file) ? 'success' : 'error'">{{ mx_icons.mandatoryToReadIcon }} </v-icon>
        </div>

        <!-- unread / quittieren dot -->
        <div v-else-if="isToAcknowledge(file)" :class="`dot ${theme.toAcknowledgeBadgeColor}`">
          <div class="inner-dot"></div>
        </div>
        <div v-else-if="isUnread(file)" :class="`dot ${theme.unreadBadgeColor}`">
          <div :class="{ 'inner-dot': true, dark: this.darkMode, light: !this.darkMode, full: file.isRelevant == 0 }"></div>
        </div>
      </template>
    </div>
    <div class="op-content" :style="'right:' + (favoritesEnabled ? '50px' : 0)" v-longpress.stop.prevent="toggleSlideOver">
      <div class="op-content-title" :class="isUnread(file) ? 'font-weight-bold' : ''">{{ getFileTitle(file) }}</div>

      <div class="op-content-inner" v-if="showMutationInfo">
        <!-- <div style="font-size:9px">{{file._userState.favorite}}{{JSON.stringify(file._userState)}}</div> -->
        <!-- Exception if file was not downloaded -->
        <div v-if="Number(file._offline) >= 500" class="error--text font- weight-bold">{{ $t('documentNotFound') }}</div>

        <div v-if="file.subtitle" class="pr-2">{{ file.subtitle }}</div>

        <!-- location within module -->
        <div v-if="showLocation" class="pr-2 mr-3 text-capitalize" v-html="displayLocation"></div>

        <!-- quittieren text -->
        <span v-if="isToAcknowledge(file)" class="caption mr-3 text-capitalize">{{ $t('requiresAcknowledgment') }}</span>

        <!-- mandatory to read text -->
        <span v-else-if="isMandatoryToReadAndNotRead(file)" class="error--text">{{ $t('mandatoryToRead') }}</span>

        <span v-else-if="isToAcknowledgeAndAcknowledged" class="caption mr-3 text-capitalize">
          {{ $t('acknowledged') }} {{ formatDate(file._userState.dateconfirmed) }}<v-icon class="mr-2" small>mdi-check</v-icon>
        </span>
        <span v-else-if="isMandatoryToReadAndRead(file)">
          <v-icon small>mdi-check</v-icon> {{ $t(readAtTextKey) }} {{ formatDate(file._userState.dateread) }} ({{ $t('mandatoryToRead') }})
        </span>

        <!-- no dateupdated display when read -->
        <span v-else-if="!isToAcknowledgeAndAcknowledged && isRead" class="caption mr-3 text-capitalize"
          >{{ $t(readAtTextKey) }} {{ formatDate(file._userState.dateread) }} <v-icon class="mr-3" small color="success">mdi-check</v-icon>
        </span>

        <span v-else-if="!isToAcknowledgeAndAcknowledged" class="caption mr-3 text-capitalize">{{ $t(notReadAtTextKey) }}</span>
        <span v-if="showPublishedDate" class="caption mr-3 text-capitalize"> {{ $t('publishedAt') }} {{ getPublishedAt(file) }}</span>
        <div v-if="additionalText">{{ additionalText }}</div>
        <div class="d-flex justify-end">
          <v-chip x-small v-for="label in file.labels" :key="label.id" :class="`label mr-2 px-2 primary ${theme.accent}`">{{ label.label }} </v-chip>

          <div v-if="mx_showTagsInDocument" class="d-flex">
            <v-chip x-small v-for="tag in file.tags" :key="tag.name" :class="`tag mr-2 px-2 ${theme.accent}`">{{ tag.name }} </v-chip>
          </div>
        </div>
      </div>
      <div class="op-content-inner" v-else>
        <div v-if="Number(file._offline) >= 500" class="error--text font- weight-bold">{{ $t('documentNotFound') }}</div>
        <div v-if="file.subtitle" class="pr-2">{{ file.subtitle }}</div>
        <!-- location within module -->
        <div v-if="showLocation" class="pr-2 mr-3 text-capitalize" v-html="displayLocation"></div>
      </div>
    </div>

    <div v-if="favoritesEnabled" class="op-right" @click.stop.prevent="">
      <!-- This is a hidden property that assures the file is rendered, namely with favorite state -->
      <!-- <div class="hidden-update-marker">{{update}}</div> -->
      <v-icon
        class="align-self-center"
        v-if="isFavorite"
        :color="`${theme.elements.activeColor}`"
        :title="$t('remove_from_favorites')"
        @click="removeFromFavorite(file)"
        >{{ mx_icons.favoriteIconActive }}
      </v-icon>
      <v-icon class="align-self-center" v-else :color="theme.elements.color" :title="$t('add_to_favorites')" @click="markAsFavorite(file)"
        >{{ mx_icons.favoriteIcon }}
      </v-icon>
    </div>

    <!-- Sliding left expander on long click -->
    <div
      v-if="false && longClickEnabled && isExpanded"
      class="expansion d-flex flex-column align-start px-3 py-2 my-0 accent"
      :class="`expander ${isExpanded ? 'accent' : 'transparent'}`"
      :style="`transform:${isExpanded ? 'translateX(0%)' : 'translateX(-200%)'}`"
      @click.stop.prevent="collapseSlideOver"
    >
      <!-- Offline/online available-->
      <div class="d-flex flex-row align-start mb-1">
        <v-icon dark color="white" small class="pr-2" style="width: 40px"> mdi-{{ file._offline ? 'cloud-check-outline' : 'cloud-outline' }} </v-icon>

        <div class="d-flex flex-row" v-if="file._offline === true">
          <div>Dokument (ID {{ file.id }}) ist <b>offline</b> verfügbar.</div>
          <div>(Sync {{ formatMillisSinceEpoch(file._fetchTime) }})</div>
        </div>
        <div v-else>Dokument ({{ file.id }}) ist nur <b>online</b> verfügbar.</div>
      </div>
      <div v-if="file._fetchTime"></div>

      <!-- Date read -->
      <div class="d-flex align-start mb-1" v-if="file._userState && file._userState.dateread">
        <v-icon class="pr-2" style="width: 40px" small color="white"> mdi-check</v-icon>
        <div v-if="file.quittierung === 1 && file._userState.dateconfirmed">
          quittiert
          {{ formatDate(file._userState.dateconfirmed) }}
        </div>
        <div v-else>gelesen {{ formatDate(file._userState.dateread) }}</div>
      </div>

      <div class="d-flex align-start mb-1" v-else>
        <v-icon style="width: 40px" small color="white"> mdi-eye-check</v-icon>
        <div>{{ $t(notReadAtTextKey) }}</div>
      </div>
      <div class="d-flex align-start mb-1">
        <v-icon style="width: 40px" small color="white"> mdi-create</v-icon>
        <div>erstellt {{ formatDate(file.datecreated) }}, aktualisiert {{ formatDate(file.dateupdated) }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import mandatoryToReadService from '@/services/mandatoryToReadService';
import readService from '@/services/readService';
import acknowledgeService from '@/services/acknowledgeService';
import favoritesService from '@/services/favoritesService';
import common from '@/mixins/common';
import configMixin from '@/mixins/configMixin';
import dateTimeMixin from '@/mixins/dateTimeMixin';
import eventBus from '@/common/eventBus';
import uiHelpers from '@/common/uiHelpers';

let collapseTime;

export default {
  name: 'File',
  mixins: [common, configMixin, dateTimeMixin],
  components: {},

  props: {
    updateCount: Number,
    file: Object,
    ignoreReadState: { type: Boolean, default: false }, // true to not display unread, confirm and mandatory to read bubbles
    showMutationInfo: { type: Boolean, default: true },

    isSelected: {
      type: Boolean,
      default: false,
    },
    teaserUrl: String,
    avatarUrl: String,
    favoritesEnabled: { type: Boolean, default: true },
    longClickEnabled: { type: Boolean, default: true }, // true allows to display extended info by long click and sliding information panel over this item
    itemIcon: { type: String, default: 'mdi-file-document-outline' },
    showLocation: { type: Boolean, default: false },
    showPublishedDate: { type: Boolean, default: true },
    additionalText: String,
    readAtTextKey: { type: String, default: 'readAt' }, //this is the text key to use for telling when the item has been read (or processed or the like)
    notReadAtTextKey: { type: String, default: 'notRead' }, //this is the text key to use for telling when the item has been read (or processed or the like)
    markAsReadOnSelect: { type: Boolean, default: true },
  },

  beforeDestroy() {
    eventBus.removeEventHandler(eventBus.createFileChangedEventType(this.file));
  },
  data() {
    return {
      filter: '',
      isLongPress: false,
      isExpanded: false,
      expandedContentIsVisible: false,
      isRead: readService.isRead(this.file),
      isFavorite: favoritesService.isFavorite(this.file),
    };
  },

  computed: {
    module() {
      return this.getModuleConfig(this.moduleType, this.file.instance);
    },
    displayLocation() {
      let file = this.file;
      if (this.file._displayLocation) {
        return this.$t('file.location', {
          moduleTitle: this.$t(file._moduleTitle),
          folder: file._displayLocation.slice(-1) == '/' ? file._displayLocation.slice(0, -1) : file._displayLocation,
        });
      }
      return this.$t('file.moduleOnly', { moduleTitle: this.$t(file._moduleTitle) });
    },
    //can be computed because no changes
    isMandatoryToRead() {
      return mandatoryToReadService.isMandatoryToRead(this.file);
    },
    isToAcknowledgeAndAcknowledged() {
      return acknowledgeService.isToAcknowledgeAndAcknowledged(this.file);
    },
  },
  methods: {
    init(newValue, oldValue) {
      // console.debug("init file", this.file.id)
      // If the new file references not the same object as before, we need to register the eventHandler
      // If the oldValue was not null, we need to remove the old handler
      if (newValue !== oldValue) {
        if (oldValue) {
          eventBus.removeEventHandler(eventBus.createFileChangedEventType(oldValue));
        }
        // console.debug("addEventHandler for", eventBus.createFileChangedEventType(this.file))
        eventBus.addEventHandler(eventBus.createFileChangedEventType(this.file), this.eventHandler);
        eventBus.addEventHandler(eventBus.createReadStateEventType(this.file), this.eventHandler);
      }

      if (newValue) {
        this.isFavorite = favoritesService.isFavorite(this.file);
        this.isRead = readService.isRead(this.file);
      }
    },
    /**
     * The FileChanged eventHandler.
     * @param {String} eventType - the eventType, this eventHandler has been registered for
     * @param {Object} eventObj - an arbitrary event object. In this case an msg and the item property is sent
     */
    eventHandler(eventType, eventObj) {
      //console.debug("received event", eventType, eventObj.msg, null, 2);
      this.isFavorite = favoritesService.isFavorite(this.file);
      this.isRead = readService.isRead(this.file);
    },
    publishEvent() {
      console.debug('publish FileChanged Event', this.file.id);
      eventBus.publish({
        selfHandler: this.eventHandler,
        eventType: eventBus.createFileChangedEventType(this.file),
        eventObj: { msg: 'favorite changed', item: this.file },
      });
    },

    //keep methods to get states. Computation leads due to caching to wrong results.

    isUnread(file) {
      return readService.isUnread(file);
    },

    isMandatoryToReadAndNotRead(file) {
      return mandatoryToReadService.isMandatoryToReadAndNotRead(file);
    },
    isMandatoryToReadAndRead(file) {
      return mandatoryToReadService.isMandatoryToReadAndRead(file);
    },

    isToAcknowledge(file) {
      return acknowledgeService.isToAcknowledge(file);
    },

    formatDate(isoDateString) {
      return this.formatIsoDateStringAsLocaleShortDate(isoDateString);
    },
    onItemSelected() {
      // long press comes before click and if so, the isLongPress flag needs to be cleared to be ready for the next real click
      if (!this.isLongPress) {
        this.onSelected();
      } else {
        this.isLongPress = false;
      }
      if (this.markAsReadOnSelect && !acknowledgeService.isToAcknowledge(this.file)) {
        readService.markAsRead(this.file);
        uiHelpers.initiateDelayedMarkAsRead(this.$store.dispatch, this.file);
      }
    },
    async onSelected() {
      await this.$emit('onSelected', this.file);
    },
    collapseSlideOver() {
      this.isExpanded = false;
      collapseTime = Date.now();
    },
    toggleSlideOver() {
      let now = Date.now();
      let timeSinceLastCollapse = now - (collapseTime ? collapseTime : 0);

      if (this.longClickEnabled && timeSinceLastCollapse > 2000) {
        this.isLongPress = true;
        this.isExpanded = true;
      }
    },
    formatMillisSinceEpoch(millisSinceEpoch) {
      return this.formatAsLocaleDateTime(new Date(millisSinceEpoch));
    },

    async markAsFavorite(file) {
      let sendboxTitle = this.$t('action.addFavorite.msg', { title: file.title });
      this.isFavorite = true;
      await this.$store.dispatch('sendbox/markAsFavorite2', { item: file, sendboxTitle, snackbarText: '' }, { root: true });
      this.publishEvent();
    },
    async removeFromFavorite(file) {
      let sendboxTitle = this.$t('action.removeFavorite.msg', { title: file.title });
      this.isFavorite = false;
      await this.$store.dispatch(
        'sendbox/removeFromFavorite2',
        {
          item: file,
          sendboxTitle,
          snackbarText: '',
        },
        { root: true }
      );
      this.publishEvent();
    },
    getFileTitle(file) {
      if (file?.properties?.name) {
        return file.properties.name;
      }
      return file.title;
    },
    getPublishedAt(file) {
      if (file.updatedAt) {
        return this.formatDate(file.updatedAt);
      }
      return this.formatDate(file.dateupdated);
    },
  },
  watch: {
    file: {
      handler(newValue, oldValue) {
        // console.debug("FILE ", newValue.id, "changed", newValue.title, newValue._userState)

        this.init(newValue, oldValue);
      },
      deep: true,
      immediate: true,
    },
    updateCount(newValue) {
      this.isFavorite = favoritesService.isFavorite(this.file);
      console.debug('FILE ', this.file.title, ' changed, calculate isFavorite', this.isFavorite);
    },
  },
};
</script>
<style scoped>
.file {
  border-bottom: 1px solid #d0d0d0;
}

.theme--dark .file {
  border-bottom: 1px solid #101010;
}

.file-wrapper-dark,
.file-wrapper-light {
  position: relative;
  transition: 0.5s;
  height: 6.2em;
  width: 100%;
  /* border: 1px solid red; */
}

.file-wrapper-light:hover {
  background: #d5d5d5 !important;
  transform: translateX(2px);
  z-index: 1000;
  opacity: 1;
}

.file-wrapper-dark:hover {
  background: #303030 !important;
  transform: translateX(2px);
  z-index: 1000;
  opacity: 1;
}

.selected-dark {
  background: #101010 !important;
}

.selected-light {
  background: #d0d0d0 !important;
}

.file-icon {
  position: absolute;
  left: 14px;
  top: 4px;
}

.dot {
  position: absolute;
  left: 31px;
  top: 2px;
  border-radius: 6px;
  width: 12px;
  height: 12px;
}

.inner-dot {
  position: absolute;
  top: 1px;
  left: 1px;
  border-radius: 5px;
  border: 1px solid #ffffff;
  width: 10px;
  height: 10px;
}

.inner-dot.light {
  border: 1px solid #ffffff;
}

.inner-dot.dark {
  border: 1px solid #101010;
}

.inner-dot.full.light {
  background: #ffffff;
}
.inner-dot.full.dark {
  background: #101010;
}

.icon-badge {
  position: absolute;
  left: 31px;
  top: -7px;
}

/* NO SELECTION + HIGHLIGHT COLOR ==> avoids copy/paste functions which is inappropriate on smart phones*/
* {
  user-select: none;
}

.teaser-img {
  position: absolute;
  left: 3px;

  top: 5px;
  height: 24px;
  width: 24px;
  /* right: 3px; */
  /* bottom: 5px; */
  background-size: contain !important;
  background-position: center !important;
  background-repeat: no-repeat !important;
  overflow: hidden;
  /* border: 1px solid purple; */
  border-radius: 50%;
}

.label,
.tag {
  font-size: 12px;
  border-radius: 10px;
  border: 2px solid white;
  color: white;
}

.expander {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 1002 !important;
  overflow: hidden;
  display: flex;
  transform: translateX(-90%);
  transition: all 300ms ease-in;
  transition-delay: 500ms;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.7rem !important;
  letter-spacing: 0.0333333333em !important;
  line-height: 1rem;
  font-weight: 400;
  font-family: Roboto, sans-serif !important;
  color: white;
}
</style>
